import CircularProgress from '@material-ui/core/CircularProgress';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import classNames from 'classnames';
import qs from 'querystringify';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import ChatSearchPanel from 'src/components/Chat/ChatSearchPanel';
import { Dispatch, store } from 'src/config/Store';
import { FetchStatus } from 'src/models/Chat';
import {
  getActiveConversation,
  getConversations,
  getFetchStatus,
  getIsSearching,
} from 'src/selectors/chatSelectors';
import { getCurrentUser } from 'src/selectors/userSelectors';

import { PaginatedChatMemberList } from '../components/Chat/PaginatedChatMemberList';
import MemberProfileDrawer from '../components/MemberProfileDrawer/MemberProfileDrawer';
import PageContainer from '../components/PageContainer';
import ChatMessagesPanel from './ChatMessagesPanel';

const styles = (theme) =>
  createStyles({
    membersPanel: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      minWidth: 100,
      maxWidth: 410,
      [theme.breakpoints.down('sm')]: {
        position: 'absolute',
        height: '100%',
        width: '100%',
        zIndex: 2,
        background: 'white',
        maxWidth: 'inherit',
        minWidth: 'inherit',
      },
    },
    membersPanelNoPagination: {
      overflowY: 'scroll',
    },
    membersPanelCloseMobile: {
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    noConversations: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      padding: 40,
    },
  });

interface MatchParams {
  conversationUuid?: string;
}

interface QueryParams {
  memberProfileDrawerVisible?: string;
}

type Props = RouteComponentProps<MatchParams> & WithStyles<typeof styles>;

const Chat: React.FC<Props> = (props) => {
  const dispatch = useDispatch<Dispatch>();
  const self = useSelector(getCurrentUser);

  const activeConversation = useSelector(getActiveConversation);
  const conversations = useSelector(getConversations);
  const isSearching = useSelector(getIsSearching);
  const conversationFetchStatus = useSelector(getFetchStatus);
  const setActiveConversationUuid = dispatch.chat.setActiveConversation;

  const [membersPanelHiddenMobile, setMembersPanelHiddenMobile] = useState(true);
  const [memberProfileDrawerVisible, setMemberProfileDrawerVisible] = useState(false);

  useEffect(() => {
    // Sync state with query params
    const params: QueryParams = qs.parse(props.location.search);
    setMemberProfileDrawerVisible(!!params.memberProfileDrawerVisible);
  }, [props.location.search]);

  useEffect(() => {
    // When navigating out of the chat page reset active convo
    return () => {
      setActiveConversationUuid('');
    };
  }, []);

  useEffect(() => {
    // Set the active coversation to the one in the path if present
    const initialConversationUuid = props.match.params.conversationUuid ?? null;
    if (
      !activeConversation ||
      (!!initialConversationUuid && initialConversationUuid !== activeConversation?.uuid)
    ) {
      setActiveConversationUuid(initialConversationUuid);
    }
  }, [props.match.params.conversationUuid, activeConversation, setActiveConversationUuid]);

  useEffect(() => {
    activeConversation &&
      store.dispatch.resources.fetchCompanyAdditionalBenefits(activeConversation.member?.uuid);
  }, [activeConversation]);

  const showMembersList = () => {
    setMembersPanelHiddenMobile(false);
  };

  const hideMembersList = () => {
    setMembersPanelHiddenMobile(true);
  };

  const showMemberProfileDrawer = () => {
    // get and update params
    const params: QueryParams = qs.parse(props.location.search);

    // update history
    props.history.replace({
      pathname: props.location.pathname,
      search: qs.stringify(
        {
          ...params,
          memberProfileDrawerVisible: true,
        },
        true,
      ),
    });
  };

  const hideMemberProfileDrawer = () => {
    // get and update params
    const params: QueryParams = qs.parse(props.location.search);
    delete params.memberProfileDrawerVisible;

    // update history
    props.history.replace({
      pathname: props.location.pathname,
      search: qs.stringify(params, true),
    });
  };

  const { classes } = props;

  if (
    !isSearching &&
    (!conversations || (!conversations.length && conversationFetchStatus === FetchStatus.Completed))
  ) {
    return (
      <PageContainer position="inherit">
        <Typography variant="body2" className={classes.noConversations}>
          You currently have no conversations with any members. Please try again when you have been
          assigned members.
        </Typography>
      </PageContainer>
    );
  }

  return (
    <PageContainer position="inherit">
      <div
        className={classNames(classes.membersPanel, {
          [classes.membersPanelCloseMobile]: membersPanelHiddenMobile && activeConversation,
        })}
      >
        <ChatSearchPanel />
        {conversationFetchStatus === FetchStatus.Loading && (
          <div className="flex justify-center py-3">
            <CircularProgress size={30} />
          </div>
        )}
        <PaginatedChatMemberList hideMembersList={hideMembersList} />
      </div>

      <div className="flex flex-grow flex-col border-l border-gray-300 bg-gray-100">
        <ChatMessagesPanel
          {...props}
          showMembersList={showMembersList}
          showMemberProfileDrawer={self.isProvider && showMemberProfileDrawer}
        />
      </div>

      {memberProfileDrawerVisible && activeConversation && (
        <MemberProfileDrawer
          onClickExit={hideMemberProfileDrawer}
          memberUuid={activeConversation.member.uuid}
        />
      )}
    </PageContainer>
  );
};

export default withStyles(styles)(Chat);
